<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { AppRoute } from '@/libs/routing';
import { useRouter } from 'vue-router';
import { useAppStore } from '@/stores/app';
import type { Group } from '@/@types/group';
import api from '@/api';
import SelectGroupMap from './SelectGroupMap.vue';
import SelectGroupDrawer from './SelectGroupDrawer.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import BackButton from '@/components/ui/BackButton.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';

const router = useRouter();
const appStore = useAppStore();

const groups = ref<Group[]>([]);
const hasLoadError = ref(false);
const isLoadingGroups = ref(true);
const isSearchMode = ref(false);
const sheetHeight = ref(0);
const mapRef = ref<InstanceType<typeof SelectGroupMap> | null>(null);

const userPosition = computed(() => appStore.positionGeolocation);
const isGeolocated = computed<boolean>(() => Boolean(userPosition.value));
const hasRouterHistory = computed(() => !!router.options.history.state.back);

async function loadGroups() {
  hasLoadError.value = false;
  isLoadingGroups.value = true;
  try {
    const result = await api.getGroups();
    groups.value = result.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  } catch {
    hasLoadError.value = true;
  } finally {
    isLoadingGroups.value = false;
  }
}

onBeforeMount(() => {
  appStore.getGeolocationIfAllowed();
  loadGroups();
});

async function handleGpsClick() {
  if (userPosition.value) {
    mapRef.value?.recenterOnNearbyGroups();
    return;
  }
  await appStore.getGeolocation();
  mapRef.value?.recenterOnNearbyGroups();
}

function handleSelectGroup(group: Group) {
  localStorage.setItem('group', JSON.stringify(group));
  router.push({ name: AppRoute.MAP, params: { groupId: group.group_id } });
}
</script>

<template>
  <div class="page page--no-navbar select-group-page">
    <SelectGroupMap
      ref="mapRef"
      :user-position="userPosition"
      :groups="groups"
      :bottom-inset="sheetHeight"
      @selectGroup="handleSelectGroup"
    />

    <BackButton v-if="hasRouterHistory" />

    <RoundedButton
      v-if="!isSearchMode"
      class="select-group-page__gps"
      type="outline"
      size="large"
      :aria-label="$t('selectGroup.locateMe')"
      @click="handleGpsClick"
    >
      <span v-if="isGeolocated" class="map__position-indicator" />
      <BaseIcon v-else name="map-pin" />
    </RoundedButton>

    <div class="select-group-page__sheet-area">
      <SelectGroupDrawer
        :groups="groups"
        :user-position="userPosition"
        :has-load-error="hasLoadError"
        :is-loading="isLoadingGroups"
        :is-location-denied="appStore.isGeolocationDenied"
        @selectGroup="handleSelectGroup"
        @retry="loadGroups"
        @locate="handleGpsClick"
        @searchFocus="isSearchMode = true"
        @searchClose="isSearchMode = false"
        @sheetHeight="sheetHeight = $event"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.select-group-page {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;

  &__gps {
    position: fixed;
    top: calc(12px + var(--status-bar-band));
    right: 16px;
    z-index: 1;
    // The position indicator is sized in em, like the glyph it replaces.
    font-size: 24px;
  }

  // `.page > *` re-enables pointer events on every direct child; the sheet's
  // playground must stay transparent to them so the map is pannable around it.
  &__sheet-area {
    @include sheet-host;
  }
}
</style>
